import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import disableDevtool from 'disable-devtool';
import { environment } from '../../../../environments/environment';
import { Md5BypassService } from './md5-bypass.service';

declare global {
  interface Window {
    dtrum?: {
      enterAction: (name: string) => number;
      leaveAction: (actionId: number) => void;
      reportError: (error: Error) => void;
      getAndClearMarks: () => unknown[];
      addEnterActionListener: (callback: (actionId: number, name: string) => void) => void;
      identifyUser: (userId: string) => void;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class DevtoolsDetectorService {
  private readonly router = inject(Router);
  private readonly md5BypassService = inject(Md5BypassService);
  private isDynatracePresent = false;
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (!environment.disableDevTools) return;
    if (this.isDynatraceSynthetic()) return;
    if (this.md5BypassService.checkMd5Bypass()) return;

    this.blockContextMenu();

    const initDelay = environment.dynatraceConfig?.initDelay || 1500;
    setTimeout(() => {
      this.isDynatracePresent = this.detectDynatraceAgent();
      this.initializeProtection();
    }, initDelay);
  }

  private blockContextMenu(): void {
    document.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  private isDynatraceSynthetic(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const syntheticPatterns = [
      'dynatracesynthetic',
      'dynatrace synthetic',
      'ruxitsynthetic',
      'dynatrace',
    ];
    return syntheticPatterns.some((pattern) => userAgent.includes(pattern));
  }

  private detectDynatraceAgent(): boolean {
    if (typeof window !== 'undefined' && window.dtrum) return true;

    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src || '';
      if (src.includes('dynatrace') || src.includes('ruxitagent')) return true;
    }
    return false;
  }

  private initializeProtection(): void {
    try {
      const md5Hash = environment.md5Config ? this.md5BypassService.generateMd5Hash() : undefined;

      const config: Parameters<typeof disableDevtool>[0] = {
        md5: md5Hash,
        tkName: environment.md5Config?.urlParamName || 'ddtk',
        ondevtoolopen: () => this.handleDevToolsOpened(),
        interval: this.isDynatracePresent ? 3000 : 1000,
        disableMenu: true,
        disableSelect: true,
      };

      if (this.isDynatracePresent) {
        (config as Record<string, unknown>)['disableIframeParents'] = false;
        (config as Record<string, unknown>)['clearIntervalWhenDevOpenTrigger'] = false;
      }

      disableDevtool(config);
    } catch (error) {
      console.error('Error al inicializar el detector de DevTools:', error);
    }
  }

  private handleDevToolsOpened(): void {
    if (!environment.devToolsRedirectUrl) {
      window.location.reload();
      return;
    }
    if (this.isExternalUrl(environment.devToolsRedirectUrl)) {
      window.location.href = environment.devToolsRedirectUrl;
    } else {
      this.router.navigateByUrl(environment.devToolsRedirectUrl);
    }
  }

  private isExternalUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  isDynatraceActive(): boolean {
    return this.isDynatracePresent;
  }
}
